//.src/components/Navbar.js
import React, { useState, useEffect, useCallback } from 'react'; // Removed useRef
import SafeLink from './SafeLink';
import { useAuth } from './../context/AuthContext';
import { useData } from './../context/DataContext';
import { apiFetchPublicCompanyInfo } from '../api/CompanySidebarApi'; // Adjust import path if needed
// REMOVE: import UserProfile from "./Auth/UserProfile"; // <-- REMOVE THIS
import './Navbar.css';

// Removed isProfileBoxVisible, setIsProfileBoxVisible, profileBoxRef, handleClickOutside, handleKeyDown

function Navbar({ handleLogout }) {
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { chatInProgress, userProfile, loading } = useData(); // Still use userProfile for welcome text
  const { currentUser } = useAuth();

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setIsHidden(true);
    } else {
      setIsHidden(false);
    }
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  const [companyInfo, setCompanyInfo] = useState(null); // { companyName, logoUrlToDisplay }
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoLoadError, setLogoLoadError] = useState(false); // Track image loading errors
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  console.log('[Navbar Debug] API Base URL from Env:', BACKEND_URL);

  useEffect(() => {
    const fetchInfo = async () => {
      setIsLoading(true);
      setError(null);
      setLogoLoadError(false);
      console.log('[WelcomePage] Fetching public company info...');
      try {
        const result = await apiFetchPublicCompanyInfo();
        if (result.status && result.data) {
          console.log('[WelcomePage] Received company info:', result.data);
          setCompanyInfo(result.data);
        } else {
          console.warn(
            '[WelcomePage] Failed to fetch company info or no data found:',
            result.message,
          );
          // Don't set an error for simply 'not found', just show defaults
          if (result.error && result.status !== 404) {
            // Set error for actual fetch errors
            setError(result.message || 'Could not load company information.');
          }
          setCompanyInfo(null); // Ensure info is null if fetch fails or empty
        }
      } catch (err) {
        console.error('[WelcomePage] Unexpected error fetching info:', err);
        setError('An unexpected error occurred.');
        setCompanyInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInfo();
  }, []); // Empty dependency array means run once on mount

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Simplified renderWelcomeContent - just displays text or photo, not a button triggering dropdown
  const renderWelcomeContent = () => {
    if (userProfile) {
      if (userProfile.photoURL) {
        return (
          <div
            className="profile-display-navbar"
            title={`${userProfile.userName}'s profile`}
          >
            <img
              src={userProfile.photoURL}
              alt={`${userProfile.userName}'s profile`}
              className="profile-picture-navbar"
            />
          </div>
        );
      } else {
        return (
          <span className="welcome-message-navbar">
            Welcome, {userProfile.userName}
          </span>
        );
      }
    } else if (currentUser && !loading) {
      const getUsernameFromEmail = (email) => {
        if (!email) return null;
        return email.split('@')[0];
      };
      return (
        <span className="welcome-message-navbar">
          Welcome, {getUsernameFromEmail(currentUser.email)}
        </span>
      );
    } else {
      return null; // Or a loading indicator
    }
  };

  const handleLogoError = (e) => {
    console.warn('[WelcomePage] Failed to load company logo image.');
    console.error(
      '[Navbar Debug] Image failed to load. Attempted URL:',
      e.target.src,
    );
    setLogoLoadError(true); // Set flag to hide broken image icon
  };

  const companyName = companyInfo?.companyName;
  const logoUrl = companyInfo?.logoUrlToDisplay;
  // DEBUG LOG 3: Check the value before rendering
  if (logoUrl) console.log('[Navbar Debug] logoUrl value from state:', logoUrl);

  // Helper to construct the final URL safely
  const getFinalLogoUrl = (rawUrl) => {
    if (!rawUrl) return null;
    if (rawUrl.startsWith('http')) return rawUrl;

    // Clean up slashes to prevent "http://localhost:8080//path"
    const cleanBase = BACKEND_URL.endsWith('/')
      ? BACKEND_URL.slice(0, -1)
      : BACKEND_URL;
    const cleanPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;

    return `${cleanBase}${cleanPath}`;
  };

  const finalLogoSrc = getFinalLogoUrl(logoUrl);
  if (finalLogoSrc)
    console.log('[Navbar Debug] Calculated <img> src:', finalLogoSrc);

  return (
    <nav
      className={`navbar ${isHidden ? 'hidden' : ''}`}
      aria-label="Main Navigation"
    >
      <div className="navbar-content">
        {logoUrl && !logoLoadError && (
          <div className="navbar-user">
            <img
              src={
                logoUrl.startsWith('http')
                  ? logoUrl
                  : `${BACKEND_URL}${logoUrl}`
              }
              alt={companyName ? `${companyName} Logo` : 'Company Logo'}
              className="influencersforce-logo"
              onError={handleLogoError}
            />
          </div>
        )}
        <div className="navbar-links">
          {currentUser ? (
            <>
              <SafeLink to="/" hasUnsavedChat={chatInProgress}>
                To-Do
              </SafeLink>
              <SafeLink to="/discovery" hasUnsavedChat={chatInProgress}>
                Discovery
              </SafeLink>
              <SafeLink to="/query" hasUnsavedChat={chatInProgress}>
                Select
              </SafeLink>
              <SafeLink to="/influencer" hasUnsavedChat={chatInProgress}>
                Profile
              </SafeLink>{' '}
              {/* Keep this link for other "Profile" related pages if they exist */}
              <SafeLink to="/publication" hasUnsavedChat={chatInProgress}>
                Content
              </SafeLink>
              <SafeLink to="/messages" hasUnsavedChat={chatInProgress}>
                Relationship
              </SafeLink>
              <SafeLink to="/knowledge" hasUnsavedChat={chatInProgress}>
                Knowledge
              </SafeLink>
              <SafeLink to="/email" hasUnsavedChat={chatInProgress}>
                Contact
              </SafeLink>
              <SafeLink to="/ai-tools" hasUnsavedChat={chatInProgress}>
                AI Chat
              </SafeLink>
              <SafeLink to="/ai-agent" hasUnsavedChat={chatInProgress}>
                AI Agent
              </SafeLink>
            </>
          ) : (
            <>
              <SafeLink to="/login" hasUnsavedChat={false}>
                Login
              </SafeLink>
              <SafeLink to="/signup" hasUnsavedChat={false}>
                Sign Up
              </SafeLink>
              <SafeLink to="/forgot-password" hasUnsavedChat={false}>
                Forgot Password
              </SafeLink>
            </>
          )}
        </div>
        {currentUser && (
          <div className="navbar-user">
            {/* Display welcome message or photo directly, no click handler for profile modal */}
            {renderWelcomeContent()}
            <button onClick={handleLogout} className="logout-button-navbar">
              Log Out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
